/**
 * Data Sensitivity API Service
 *
 * Provides API functions for data sensitivity management with automatic fallback to mock data.
 * Tries real backend first, falls back to mock data if unavailable.
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

/**
 * Get authentication headers with JWT token if available
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

/**
 * Comprehensive mock data matching backend structure
 */
const MOCK_DATA = {
  stats: {
    data: {
      totalClassifiedFields: 247,
      highRiskFieldsCount: 18,
      activePermissionSets: 12,
      lastAuditDate: '2025-10-14T09:00:00Z',
      byClassification: {
        public: 102,
        internal: 89,
        confidential: 38,
        restricted: 12,
        highlyRestricted: 6
      }
    },
    _usingMockData: true
  },

  fields: {
    data: [
      {
        id: 'field_001',
        objectName: 'Client',
        objectApiName: 'Client',
        fieldName: 'Annual Revenue',
        fieldApiName: 'annualRevenue',
        fieldType: 'Currency',
        isCustom: false,
        isRequired: false,
        classification: 'confidential',
        isEncrypted: false,
        maskingRule: 'none',
        createdDate: '2024-10-15T10:30:00Z',
        lastModifiedDate: '2025-01-10T14:22:00Z',
        lastModifiedBy: {
          id: 'user_001',
          name: 'Jane Smith'
        },
        usageStats: {
          recordsWithData: 1247,
          totalRecords: 1500,
          percentPopulated: 83.13,
          lastAccessed: '2025-10-14T16:45:00Z'
        },
        permissions: {
          canRead: ['admin', 'sales_manager', 'finance'],
          canEdit: ['admin', 'finance']
        },
        dependencies: {
          reports: 3,
          workflows: 1,
          integrations: 2
        }
      },
      {
        id: 'field_002',
        objectName: 'Client',
        objectApiName: 'Client',
        fieldName: 'Tax ID',
        fieldApiName: 'taxId',
        fieldType: 'Text',
        isCustom: false,
        isRequired: false,
        classification: 'highlyRestricted',
        isEncrypted: true,
        maskingRule: 'last4',
        createdDate: '2024-08-20T09:15:00Z',
        lastModifiedDate: '2025-01-15T11:30:00Z',
        lastModifiedBy: {
          id: 'user_002',
          name: 'John Doe'
        },
        usageStats: {
          recordsWithData: 892,
          totalRecords: 1500,
          percentPopulated: 59.47,
          lastAccessed: '2025-10-15T08:22:00Z'
        },
        permissions: {
          canRead: ['admin', 'finance'],
          canEdit: ['admin']
        },
        dependencies: {
          reports: 1,
          workflows: 0,
          integrations: 1
        }
      },
      {
        id: 'field_003',
        objectName: 'Contact',
        objectApiName: 'Contact',
        fieldName: 'Email',
        fieldApiName: 'email',
        fieldType: 'Email',
        isCustom: false,
        isRequired: true,
        classification: 'internal',
        isEncrypted: false,
        maskingRule: 'none',
        createdDate: '2023-05-10T10:00:00Z',
        lastModifiedDate: '2024-12-01T09:45:00Z',
        lastModifiedBy: {
          id: 'user_001',
          name: 'Jane Smith'
        },
        usageStats: {
          recordsWithData: 2845,
          totalRecords: 2900,
          percentPopulated: 98.10,
          lastAccessed: '2025-10-15T09:15:00Z'
        },
        permissions: {
          canRead: ['admin', 'sales_user', 'support', 'marketing'],
          canEdit: ['admin', 'sales_user', 'support']
        },
        dependencies: {
          reports: 15,
          workflows: 8,
          integrations: 5
        }
      },
      {
        id: 'field_004',
        objectName: 'Contact',
        objectApiName: 'Contact',
        fieldName: 'Social Security Number',
        fieldApiName: 'ssn',
        fieldType: 'Text',
        isCustom: false,
        isRequired: false,
        classification: 'highlyRestricted',
        isEncrypted: true,
        maskingRule: 'full',
        createdDate: '2024-09-05T14:20:00Z',
        lastModifiedDate: '2025-02-01T10:15:00Z',
        lastModifiedBy: {
          id: 'user_003',
          name: 'Sarah Johnson'
        },
        usageStats: {
          recordsWithData: 456,
          totalRecords: 2900,
          percentPopulated: 15.72,
          lastAccessed: '2025-10-13T11:30:00Z'
        },
        permissions: {
          canRead: ['admin'],
          canEdit: ['admin']
        },
        dependencies: {
          reports: 0,
          workflows: 1,
          integrations: 0
        }
      },
      {
        id: 'field_005',
        objectName: 'Contact',
        objectApiName: 'Contact',
        fieldName: 'Phone',
        fieldApiName: 'phone',
        fieldType: 'Phone',
        isCustom: false,
        isRequired: false,
        classification: 'internal',
        isEncrypted: false,
        maskingRule: 'none',
        createdDate: '2023-05-10T10:00:00Z',
        lastModifiedDate: '2024-11-20T15:30:00Z',
        lastModifiedBy: {
          id: 'user_001',
          name: 'Jane Smith'
        },
        usageStats: {
          recordsWithData: 2654,
          totalRecords: 2900,
          percentPopulated: 91.52,
          lastAccessed: '2025-10-15T08:45:00Z'
        },
        permissions: {
          canRead: ['admin', 'sales_user', 'support'],
          canEdit: ['admin', 'sales_user', 'support']
        },
        dependencies: {
          reports: 12,
          workflows: 4,
          integrations: 3
        }
      },
      {
        id: 'field_006',
        objectName: 'Opportunity',
        objectApiName: 'Opportunity',
        fieldName: 'Amount',
        fieldApiName: 'Amount__c',
        fieldType: 'Currency',
        isCustom: false,
        isRequired: false,
        classification: 'confidential',
        isEncrypted: false,
        maskingRule: 'none',
        createdDate: '2024-06-15T09:00:00Z',
        lastModifiedDate: '2025-01-22T13:45:00Z',
        lastModifiedBy: {
          id: 'user_004',
          name: 'Mike Wilson'
        },
        usageStats: {
          recordsWithData: 3421,
          totalRecords: 4200,
          percentPopulated: 81.45,
          lastAccessed: '2025-10-15T10:00:00Z'
        },
        permissions: {
          canRead: ['admin', 'sales_manager', 'finance'],
          canEdit: ['admin', 'sales_manager']
        },
        dependencies: {
          reports: 8,
          workflows: 3,
          integrations: 1
        }
      },
      {
        id: 'field_007',
        objectName: 'Opportunity',
        objectApiName: 'Opportunity',
        fieldName: 'Close Date',
        fieldApiName: 'CloseDate__c',
        fieldType: 'Date',
        isCustom: false,
        isRequired: true,
        classification: 'public',
        isEncrypted: false,
        maskingRule: 'none',
        createdDate: '2023-03-20T10:00:00Z',
        lastModifiedDate: '2024-10-05T11:15:00Z',
        lastModifiedBy: {
          id: 'user_001',
          name: 'Jane Smith'
        },
        usageStats: {
          recordsWithData: 4200,
          totalRecords: 4200,
          percentPopulated: 100.0,
          lastAccessed: '2025-10-15T09:30:00Z'
        },
        permissions: {
          canRead: ['admin', 'sales_user', 'sales_manager', 'marketing'],
          canEdit: ['admin', 'sales_user', 'sales_manager']
        },
        dependencies: {
          reports: 22,
          workflows: 10,
          integrations: 4
        }
      },
      {
        id: 'field_008',
        objectName: 'Contact',
        objectApiName: 'Contact',
        fieldName: 'Credit Score',
        fieldApiName: 'creditScore',
        fieldType: 'Number',
        isCustom: false,
        isRequired: false,
        classification: 'restricted',
        isEncrypted: true,
        maskingRule: 'range',
        createdDate: '2024-07-10T11:30:00Z',
        lastModifiedDate: '2025-02-10T09:20:00Z',
        lastModifiedBy: {
          id: 'user_005',
          name: 'Lisa Anderson'
        },
        usageStats: {
          recordsWithData: 1523,
          totalRecords: 5600,
          percentPopulated: 27.20,
          lastAccessed: '2025-10-14T15:10:00Z'
        },
        permissions: {
          canRead: ['admin', 'finance', 'underwriter'],
          canEdit: ['admin', 'underwriter']
        },
        dependencies: {
          reports: 4,
          workflows: 2,
          integrations: 1
        }
      }
    ],
    pagination: {
      page: 1,
      pageSize: 50,
      totalItems: 8,
      totalPages: 1,
      hasNext: false,
      hasPrev: false
    },
    _usingMockData: true
  },

  objects: {
    data: [
      {
        id: 'obj_client',
        name: 'Client',
        label: 'Client',
        apiName: 'Client'
      },
      {
        id: 'obj_contact',
        name: 'Contact',
        label: 'Contact',
        apiName: 'Contact'
      },
      {
        id: 'obj_opportunity',
        name: 'Opportunity',
        label: 'Opportunity',
        apiName: 'Opportunity'
      },
      {
        id: 'obj_lead',
        name: 'Lead',
        label: 'Lead',
        apiName: 'Lead'
      }
    ],
    _usingMockData: true
  }
};

/**
 * Fetch sensitivity statistics
 * Tries backend first, falls back to mock data
 */
export const fetchSensitivityStats = async () => {
  console.log('🔍 Fetching sensitivity stats...');

  try {
    const response = await fetch(`${API_BASE_URL}/admin/stats/sensitivity`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (response.ok) {
      const data = await response.json();
      console.log('✅ Stats loaded from backend');
      return { ...data, _usingMockData: false };
    } else if (response.status === 401) {
      console.warn('⚠️ Unauthorized (401), falling back to mock data for stats');
    } else if (response.status === 500) {
      console.error('❌ Server error (500), falling back to mock data for stats');
    } else {
      console.warn(`⚠️ Backend returned ${response.status}, falling back to mock data for stats`);
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, using mock data for stats:', error);
  }

  // Add small delay to simulate API call
  await new Promise(resolve => setTimeout(resolve, 150));
  return MOCK_DATA.stats;
};

/**
 * Fetch fields with optional filters
 * Tries backend first, falls back to mock data
 */
export const fetchFields = async (filters?: {
  object?: string;
  classification?: string;
  page?: number;
  pageSize?: number;
}) => {
  console.log('🔍 Fetching fields...', filters);

  try {
    const params = new URLSearchParams();
    if (filters?.object && filters.object !== 'all') {
      params.append('object', filters.object);
    }
    if (filters?.classification && filters.classification !== 'all') {
      params.append('classification', filters.classification);
    }
    if (filters?.page) {
      params.append('page', filters.page.toString());
    }
    if (filters?.pageSize) {
      params.append('pageSize', filters.pageSize.toString());
    }

    const url = `${API_BASE_URL}/admin/fields${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (response.ok) {
      const data = await response.json();
      console.log('✅ Fields loaded from backend');
      return { ...data, _usingMockData: false };
    } else if (response.status === 401) {
      console.warn('⚠️ Unauthorized (401), falling back to mock data for fields');
    } else if (response.status === 404) {
      console.warn('⚠️ Endpoint not found (404), returning empty results');
      return {
        data: [],
        pagination: {
          page: 1,
          pageSize: 50,
          totalItems: 0,
          totalPages: 0,
          hasNext: false,
          hasPrev: false
        },
        _usingMockData: false
      };
    } else if (response.status === 500) {
      console.error('❌ Server error (500), falling back to mock data for fields');
    } else {
      console.warn(`⚠️ Backend returned ${response.status}, falling back to mock data for fields`);
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, using mock data for fields:', error);
  }

  // Add small delay to simulate API call
  await new Promise(resolve => setTimeout(resolve, 150));

  // Apply filters to mock data
  let filteredData = [...MOCK_DATA.fields.data];

  if (filters?.object && filters.object !== 'all') {
    filteredData = filteredData.filter(field =>
      field.objectName.toLowerCase() === filters.object?.toLowerCase()
    );
  }

  if (filters?.classification && filters.classification !== 'all') {
    filteredData = filteredData.filter(field =>
      field.classification === filters.classification
    );
  }

  return {
    data: filteredData,
    pagination: {
      page: 1,
      pageSize: 50,
      totalItems: filteredData.length,
      totalPages: 1,
      hasNext: false,
      hasPrev: false
    },
    _usingMockData: true
  };
};

/**
 * Fetch objects list
 * Tries backend first, falls back to mock data
 */
export const fetchObjects = async () => {
  console.log('🔍 Fetching objects...');

  try {
    const response = await fetch(`${API_BASE_URL}/admin/objects`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (response.ok) {
      const data = await response.json();
      console.log('✅ Objects loaded from backend');
      return { ...data, _usingMockData: false };
    } else if (response.status === 401) {
      console.warn('⚠️ Unauthorized (401), falling back to mock data for objects');
    } else if (response.status === 500) {
      console.error('❌ Server error (500), falling back to mock data for objects');
    } else {
      console.warn(`⚠️ Backend returned ${response.status}, falling back to mock data for objects`);
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, using mock data for objects:', error);
  }

  // Add small delay to simulate API call
  await new Promise(resolve => setTimeout(resolve, 150));
  return MOCK_DATA.objects;
};

/**
 * Fetch detailed field information (includes audit trail and impact analysis)
 */
export const fetchFieldDetails = async (fieldId: string) => {
  console.log('🔍 Fetching field details for:', fieldId);

  try {
    const response = await fetch(`${API_BASE_URL}/admin/fields/${fieldId}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (response.ok) {
      console.log('✅ Field details loaded from backend');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, using mock field details');
  }

  // Mock detailed field data
  await new Promise(resolve => setTimeout(resolve, 150));

  // Find the field in mock data
  const field = MOCK_DATA.fields.data.find(f => f.id === fieldId);
  if (!field) {
    throw new Error('Field not found');
  }

  // Return enhanced field with additional details
  return {
    data: {
      ...field,
      auditTrail: [
        {
          id: 'audit_001',
          action: 'classification_changed',
          changedBy: { id: 'user_001', name: 'Jane Smith' },
          changes: {
            classification: { from: 'internal', to: field.classification }
          },
          timestamp: '2025-10-10T14:30:00Z'
        },
        {
          id: 'audit_002',
          action: 'field_created',
          changedBy: { id: 'system', name: 'System' },
          timestamp: field.createdDate
        }
      ],
      impactAnalysis: {
        affectedUsers: 12,
        affectedReports: field.dependencies.reports,
        affectedWorkflows: field.dependencies.workflows,
        affectedIntegrations: field.dependencies.integrations,
        estimatedImpact: field.classification === 'highlyRestricted' ? 'high' : 'medium'
      }
    },
    _usingMockData: true
  };
};

/**
 * Update field classification and security settings
 */
export const updateFieldClassification = async (fieldId: string, updates: any) => {
  console.log('💾 Updating field:', fieldId, updates);

  try {
    const response = await fetch(`${API_BASE_URL}/admin/fields/${fieldId}/classification`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(updates)
    });

    if (response.ok) {
      console.log('✅ Field updated successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, simulating update');
  }

  // Mock successful update
  await new Promise(resolve => setTimeout(resolve, 300));
  return {
    data: {
      id: fieldId,
      ...updates,
      updatedAt: new Date().toISOString()
    },
    message: 'Field classification updated successfully',
    _usingMockData: true
  };
};

/**
 * Fetch all roles for permissions matrix
 */
export const fetchRoles = async () => {
  console.log('🔍 Fetching roles...');

  try {
    const response = await fetch(`${API_BASE_URL}/admin/roles`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (response.ok) {
      console.log('✅ Roles loaded from backend');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, using mock roles');
  }

  // Mock roles data
  await new Promise(resolve => setTimeout(resolve, 100));
  return {
    data: [
      { id: 'role_001', name: 'admin', label: 'System Administrator', userCount: 5 },
      { id: 'role_002', name: 'sales_manager', label: 'Sales Manager', userCount: 8 },
      { id: 'role_003', name: 'sales_user', label: 'Sales User', userCount: 25 },
      { id: 'role_004', name: 'support', label: 'Support Agent', userCount: 10 },
      { id: 'role_005', name: 'finance', label: 'Finance Team', userCount: 4 },
      { id: 'role_006', name: 'marketing', label: 'Marketing Team', userCount: 6 },
      { id: 'role_007', name: 'underwriter', label: 'Underwriter', userCount: 3 }
    ],
    _usingMockData: true
  };
};

/**
 * Get impact analysis for classification change
 */
export const getImpactAnalysis = async (fieldId: string, newClassification: string) => {
  console.log('📊 Analyzing impact for:', fieldId, 'new classification:', newClassification);

  try {
    const response = await fetch(
      `${API_BASE_URL}/admin/fields/${fieldId}/impact?newClassification=${newClassification}`,
      {
        method: 'GET',
        headers: getAuthHeaders(),
      }
    );

    if (response.ok) {
      console.log('✅ Impact analysis loaded from backend');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, using mock impact analysis');
  }

  // Mock impact analysis
  await new Promise(resolve => setTimeout(resolve, 200));

  const isHighRisk = ['restricted', 'highlyRestricted'].includes(newClassification);

  return {
    data: {
      affectedUsers: isHighRisk ? 25 : 5,
      loseAccess: isHighRisk,
      affectedReports: isHighRisk ? 12 : 3,
      affectedWorkflows: isHighRisk ? 5 : 1,
      affectedIntegrations: isHighRisk ? 3 : 0,
      highRisk: isHighRisk,
      recommendedActions: isHighRisk ? [
        'Review dependent reports and update permissions',
        'Notify affected users of access changes',
        'Update integration configurations'
      ] : [
        'No critical actions required'
      ]
    },
    _usingMockData: true
  };
};

// ============================================
// PERMISSION SETS API FUNCTIONS
// ============================================

/**
 * Fetch all permission sets
 */
export const fetchPermissionSets = async () => {
  console.log('🔍 Fetching permission sets...');

  try {
    const response = await fetch(`${API_BASE_URL}/admin/permission-sets`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (response.ok) {
      console.log('✅ Permission sets loaded from backend');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, using mock permission sets');
  }

  // Mock permission sets
  await new Promise(resolve => setTimeout(resolve, 150));
  return {
    data: [
      {
        id: 'ps_001',
        name: 'Sales Team Standard',
        description: 'Standard permissions for sales team members',
        isActive: true,
        assignedUserCount: 25,
        createdBy: { id: 'user_001', name: 'Admin User' },
        createdDate: '2024-08-15T10:00:00Z',
        lastModifiedDate: '2025-02-10T14:30:00Z'
      },
      {
        id: 'ps_002',
        name: 'Sales Manager',
        description: 'Extended permissions for sales managers',
        isActive: true,
        assignedUserCount: 8,
        createdBy: { id: 'user_001', name: 'Admin User' },
        createdDate: '2024-08-15T10:15:00Z',
        lastModifiedDate: '2025-01-20T09:45:00Z'
      },
      {
        id: 'ps_003',
        name: 'Finance Read-Only',
        description: 'Read-only access to financial data',
        isActive: true,
        assignedUserCount: 4,
        createdBy: { id: 'user_001', name: 'Admin User' },
        createdDate: '2024-09-01T11:00:00Z',
        lastModifiedDate: '2024-09-01T11:00:00Z'
      },
      {
        id: 'ps_004',
        name: 'Support Agent',
        description: 'Permissions for customer support team',
        isActive: true,
        assignedUserCount: 10,
        createdBy: { id: 'user_002', name: 'Jane Smith' },
        createdDate: '2024-10-12T13:20:00Z',
        lastModifiedDate: '2025-03-05T16:10:00Z'
      },
      {
        id: 'ps_005',
        name: 'Data Migration (Deprecated)',
        description: 'Temporary permissions for data migration - no longer in use',
        isActive: false,
        assignedUserCount: 0,
        createdBy: { id: 'user_001', name: 'Admin User' },
        createdDate: '2024-07-01T08:00:00Z',
        lastModifiedDate: '2024-12-15T17:00:00Z'
      }
    ],
    _usingMockData: true
  };
};

/**
 * Fetch detailed permission set information
 */
export const fetchPermissionSetDetails = async (permissionSetId: string) => {
  console.log('🔍 Fetching permission set details for:', permissionSetId);

  try {
    const response = await fetch(`${API_BASE_URL}/admin/permission-sets/${permissionSetId}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (response.ok) {
      console.log('✅ Permission set details loaded from backend');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, using mock permission set details');
  }

  // Mock detailed permission set
  await new Promise(resolve => setTimeout(resolve, 150));

  // Find basic info from list
  const allPermSets = await fetchPermissionSets();
  const basicInfo = allPermSets.data.find((ps: any) => ps.id === permissionSetId);

  if (!basicInfo) {
    throw new Error('Permission set not found');
  }

  return {
    data: {
      ...basicInfo,
      objectPermissions: {
        'Client': {
          read: true,
          create: true,
          edit: true,
          delete: false,
          viewAll: false,
          modifyAll: false
        },
        'Contact': {
          read: true,
          create: true,
          edit: true,
          delete: false,
          viewAll: false,
          modifyAll: false
        },
        'Opportunity': {
          read: true,
          create: permissionSetId === 'ps_001' || permissionSetId === 'ps_002',
          edit: permissionSetId === 'ps_001' || permissionSetId === 'ps_002',
          delete: permissionSetId === 'ps_002',
          viewAll: false,
          modifyAll: false
        }
      },
      fieldPermissions: {
        'annualRevenue': {
          read: permissionSetId !== 'ps_003',
          edit: permissionSetId === 'ps_002'
        },
        'taxId': {
          read: permissionSetId === 'ps_002',
          edit: false
        },
        'email': {
          read: true,
          edit: permissionSetId !== 'ps_003'
        }
      }
    },
    _usingMockData: true
  };
};

/**
 * Create new permission set
 */
export const createPermissionSet = async (permissionSetData: any) => {
  console.log('➕ Creating permission set:', permissionSetData.name);

  try {
    const response = await fetch(`${API_BASE_URL}/admin/permission-sets`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(permissionSetData)
    });

    if (response.ok) {
      console.log('✅ Permission set created successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, simulating permission set creation');
  }

  // Mock creation
  await new Promise(resolve => setTimeout(resolve, 300));
  return {
    data: {
      id: `ps_${Date.now()}`,
      ...permissionSetData,
      assignedUserCount: 0,
      createdDate: new Date().toISOString(),
      lastModifiedDate: new Date().toISOString()
    },
    message: 'Permission set created successfully',
    _usingMockData: true
  };
};

/**
 * Update permission set
 */
export const updatePermissionSet = async (permissionSetId: string, updates: any) => {
  console.log('💾 Updating permission set:', permissionSetId);

  try {
    const response = await fetch(`${API_BASE_URL}/admin/permission-sets/${permissionSetId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(updates)
    });

    if (response.ok) {
      console.log('✅ Permission set updated successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, simulating permission set update');
  }

  // Mock update
  await new Promise(resolve => setTimeout(resolve, 300));
  return {
    data: {
      id: permissionSetId,
      ...updates,
      lastModifiedDate: new Date().toISOString()
    },
    message: 'Permission set updated successfully',
    _usingMockData: true
  };
};

/**
 * Delete permission set
 */
export const deletePermissionSet = async (permissionSetId: string) => {
  console.log('🗑️ Deleting permission set:', permissionSetId);

  try {
    const response = await fetch(`${API_BASE_URL}/admin/permission-sets/${permissionSetId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (response.ok) {
      console.log('✅ Permission set deleted successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, simulating permission set deletion');
  }

  // Mock deletion
  await new Promise(resolve => setTimeout(resolve, 200));
  return {
    message: 'Permission set deleted successfully',
    _usingMockData: true
  };
};

/**
 * Clone permission set
 */
export const clonePermissionSet = async (permissionSetId: string) => {
  console.log('📋 Cloning permission set:', permissionSetId);

  try {
    const response = await fetch(`${API_BASE_URL}/admin/permission-sets/${permissionSetId}/clone`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });

    if (response.ok) {
      console.log('✅ Permission set cloned successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, simulating permission set cloning');
  }

  // Mock cloning
  await new Promise(resolve => setTimeout(resolve, 300));
  const original = await fetchPermissionSetDetails(permissionSetId);

  return {
    data: {
      ...original.data,
      id: `ps_${Date.now()}`,
      name: `${original.data.name} (Copy)`,
      assignedUserCount: 0,
      createdDate: new Date().toISOString(),
      lastModifiedDate: new Date().toISOString()
    },
    message: 'Permission set cloned successfully',
    _usingMockData: true
  };
};

/**
 * Fetch users assigned to permission set
 */
export const fetchPermissionSetUsers = async (permissionSetId: string) => {
  console.log('🔍 Fetching users for permission set:', permissionSetId);

  try {
    const response = await fetch(`${API_BASE_URL}/admin/permission-sets/${permissionSetId}/users`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (response.ok) {
      console.log('✅ Permission set users loaded from backend');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, using mock users');
  }

  // Mock users
  await new Promise(resolve => setTimeout(resolve, 100));

  const mockUsers: { [key: string]: any[] } = {
    'ps_001': [
      { id: 'user_101', name: 'John Doe', email: 'john.doe@company.com', assignedDate: '2024-09-01T10:00:00Z' },
      { id: 'user_102', name: 'Jane Smith', email: 'jane.smith@company.com', assignedDate: '2024-09-15T14:30:00Z' },
      { id: 'user_103', name: 'Bob Johnson', email: 'bob.johnson@company.com', assignedDate: '2024-10-01T09:00:00Z' }
    ],
    'ps_002': [
      { id: 'user_201', name: 'Alice Williams', email: 'alice.w@company.com', assignedDate: '2024-08-20T11:00:00Z' },
      { id: 'user_202', name: 'Charlie Brown', email: 'charlie.b@company.com', assignedDate: '2024-09-10T15:45:00Z' }
    ],
    'ps_003': [
      { id: 'user_301', name: 'David Lee', email: 'david.lee@company.com', assignedDate: '2024-09-05T08:30:00Z' }
    ],
    'ps_004': [
      { id: 'user_401', name: 'Emily Davis', email: 'emily.d@company.com', assignedDate: '2024-10-15T13:00:00Z' },
      { id: 'user_402', name: 'Frank Miller', email: 'frank.m@company.com', assignedDate: '2024-10-20T10:15:00Z' }
    ]
  };

  return {
    data: mockUsers[permissionSetId] || [],
    _usingMockData: true
  };
};

/**
 * Assign users to permission set
 */
export const assignUsersToPermissionSet = async (permissionSetId: string, userIds: string[]) => {
  console.log('👥 Assigning users to permission set:', permissionSetId, userIds);

  try {
    const response = await fetch(`${API_BASE_URL}/admin/permission-sets/${permissionSetId}/users`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ userIds })
    });

    if (response.ok) {
      console.log('✅ Users assigned successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, simulating user assignment');
  }

  // Mock assignment
  await new Promise(resolve => setTimeout(resolve, 200));
  return {
    message: `${userIds.length} user(s) assigned successfully`,
    _usingMockData: true
  };
};

/**
 * Remove user from permission set
 */
export const removeUserFromPermissionSet = async (permissionSetId: string, userId: string) => {
  console.log('➖ Removing user from permission set:', permissionSetId, userId);

  try {
    const response = await fetch(`${API_BASE_URL}/admin/permission-sets/${permissionSetId}/users/${userId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (response.ok) {
      console.log('✅ User removed successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, simulating user removal');
  }

  // Mock removal
  await new Promise(resolve => setTimeout(resolve, 150));
  return {
    message: 'User removed successfully',
    _usingMockData: true
  };
};

/**
 * Fetch all users (for assignment)
 */
export const fetchAllUsers = async (searchQuery = '') => {
  console.log('🔍 Fetching all users...', searchQuery);

  try {
    const params = new URLSearchParams();
    if (searchQuery) params.append('search', searchQuery);

    const response = await fetch(`${API_BASE_URL}/admin/users?${params.toString()}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (response.ok) {
      console.log('✅ Users loaded from backend');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, using mock users');
  }

  // Mock all users
  await new Promise(resolve => setTimeout(resolve, 100));

  const allUsers = [
    { id: 'user_101', name: 'John Doe', email: 'john.doe@company.com', role: 'Sales User' },
    { id: 'user_102', name: 'Jane Smith', email: 'jane.smith@company.com', role: 'Sales User' },
    { id: 'user_103', name: 'Bob Johnson', email: 'bob.johnson@company.com', role: 'Sales User' },
    { id: 'user_201', name: 'Alice Williams', email: 'alice.w@company.com', role: 'Sales Manager' },
    { id: 'user_202', name: 'Charlie Brown', email: 'charlie.b@company.com', role: 'Sales Manager' },
    { id: 'user_301', name: 'David Lee', email: 'david.lee@company.com', role: 'Finance' },
    { id: 'user_401', name: 'Emily Davis', email: 'emily.d@company.com', role: 'Support' },
    { id: 'user_402', name: 'Frank Miller', email: 'frank.m@company.com', role: 'Support' },
    { id: 'user_501', name: 'Grace Chen', email: 'grace.chen@company.com', role: 'Marketing' },
    { id: 'user_502', name: 'Henry Wilson', email: 'henry.w@company.com', role: 'Marketing' }
  ];

  let filtered = allUsers;
  if (searchQuery) {
    const query = searchQuery.toLowerCase();
    filtered = allUsers.filter(user =>
      user.name.toLowerCase().includes(query) ||
      user.email.toLowerCase().includes(query) ||
      user.role.toLowerCase().includes(query)
    );
  }

  return {
    data: filtered,
    _usingMockData: true
  };
};

/**
 * Fetch objects with fields (for permission set editor)
 */
export const fetchObjectsWithFields = async () => {
  console.log('🔍 Fetching objects with fields...');

  try {
    const response = await fetch(`${API_BASE_URL}/admin/objects?includeFields=true`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (response.ok) {
      console.log('✅ Objects with fields loaded from backend');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, using mock objects with fields');
  }

  // Mock objects with fields
  await new Promise(resolve => setTimeout(resolve, 150));
  return {
    data: [
      {
        id: 'obj_client',
        name: 'Client',
        label: 'Client',
        apiName: 'Client',
        fields: [
          { id: 'field_001', name: 'Annual Revenue', apiName: 'annualRevenue', label: 'Annual Revenue', type: 'Currency' },
          { id: 'field_002', name: 'Tax ID', apiName: 'taxId', label: 'Tax ID', type: 'Text' },
          { id: 'field_003', name: 'Industry', apiName: 'industry', label: 'Industry', type: 'Picklist' },
          { id: 'field_004', name: 'Phone', apiName: 'phone', label: 'Phone', type: 'Phone' }
        ]
      },
      {
        id: 'obj_contact',
        name: 'Contact',
        label: 'Contact',
        apiName: 'Contact',
        fields: [
          { id: 'field_005', name: 'Email', apiName: 'email', label: 'Email', type: 'Email' },
          { id: 'field_006', name: 'SSN', apiName: 'ssn', label: 'Social Security Number', type: 'Text' },
          { id: 'field_007', name: 'Date of Birth', apiName: 'dateOfBirth', label: 'Date of Birth', type: 'Date' },
          { id: 'field_008', name: 'Mobile', apiName: 'mobile', label: 'Mobile Phone', type: 'Phone' }
        ]
      },
      {
        id: 'obj_opportunity',
        name: 'Opportunity',
        label: 'Opportunity',
        apiName: 'Opportunity',
        fields: [
          { id: 'field_009', name: 'Amount', apiName: 'amount', label: 'Amount', type: 'Currency' },
          { id: 'field_010', name: 'Stage', apiName: 'stage', label: 'Stage', type: 'Picklist' },
          { id: 'field_011', name: 'Close Date', apiName: 'closeDate', label: 'Close Date', type: 'Date' },
          { id: 'field_012', name: 'Probability', apiName: 'probability', label: 'Probability %', type: 'Percent' }
        ]
      }
    ],
    _usingMockData: true
  };
};

// ============================================
// AUDIT LOG API FUNCTIONS
// ============================================

/**
 * Fetch audit log entries
 */
export const fetchAuditLog = async (filters: {
  actionType?: string;
  userId?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  pageSize?: number;
} = {}) => {
  console.log('🔍 Fetching audit log...', filters);

  try {
    const params = new URLSearchParams();
    if (filters.actionType) params.append('actionType', filters.actionType);
    if (filters.userId) params.append('userId', filters.userId);
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.pageSize) params.append('pageSize', filters.pageSize.toString());

    const response = await fetch(`${API_BASE_URL}/admin/audit/sensitivity?${params.toString()}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (response.ok) {
      console.log('✅ Audit log loaded from backend');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, using mock audit log');
  }

  // Mock audit log data
  await new Promise(resolve => setTimeout(resolve, 150));

  const mockAuditEntries = [
    {
      id: 'audit_001',
      timestamp: '2025-10-15T14:30:22Z',
      actionType: 'field_classification_changed',
      actionLabel: 'Field Classification Changed',
      user: {
        id: 'user_001',
        name: 'Jane Smith',
        email: 'jane.smith@company.com'
      },
      target: {
        type: 'field',
        id: 'field_001',
        name: 'Annual Revenue',
        objectName: 'Account'
      },
      changes: {
        classification: {
          from: 'internal',
          to: 'confidential'
        },
        isEncrypted: {
          from: false,
          to: true
        }
      },
      ipAddress: '192.168.1.100',
      userAgent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7)'
    },
    {
      id: 'audit_002',
      timestamp: '2025-10-15T11:15:45Z',
      actionType: 'permission_set_created',
      actionLabel: 'Permission Set Created',
      user: {
        id: 'user_001',
        name: 'Jane Smith',
        email: 'jane.smith@company.com'
      },
      target: {
        type: 'permission_set',
        id: 'ps_006',
        name: 'Marketing Team Standard'
      },
      changes: null,
      ipAddress: '192.168.1.100',
      userAgent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7)'
    },
    {
      id: 'audit_003',
      timestamp: '2025-10-15T10:22:18Z',
      actionType: 'permission_set_modified',
      actionLabel: 'Permission Set Modified',
      user: {
        id: 'user_002',
        name: 'John Doe',
        email: 'john.doe@company.com'
      },
      target: {
        type: 'permission_set',
        id: 'ps_001',
        name: 'Sales Team Standard'
      },
      changes: {
        objectPermissions: {
          'opportunity__c': {
            from: { edit: false },
            to: { edit: true }
          }
        }
      },
      ipAddress: '192.168.1.105',
      userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64)'
    },
    {
      id: 'audit_004',
      timestamp: '2025-10-14T16:45:33Z',
      actionType: 'user_assigned_to_permission_set',
      actionLabel: 'User Assigned to Permission Set',
      user: {
        id: 'user_001',
        name: 'Jane Smith',
        email: 'jane.smith@company.com'
      },
      target: {
        type: 'permission_set',
        id: 'ps_002',
        name: 'Sales Manager'
      },
      changes: {
        assignedUsers: {
          added: ['Charlie Brown']
        }
      },
      ipAddress: '192.168.1.100',
      userAgent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7)'
    },
    {
      id: 'audit_005',
      timestamp: '2025-10-14T14:20:11Z',
      actionType: 'field_classification_changed',
      actionLabel: 'Field Classification Changed',
      user: {
        id: 'user_003',
        name: 'Sarah Johnson',
        email: 'sarah.j@company.com'
      },
      target: {
        type: 'field',
        id: 'field_006',
        name: 'Social Security Number',
        objectName: 'Contact'
      },
      changes: {
        classification: {
          from: 'restricted',
          to: 'highlyRestricted'
        },
        maskingRule: {
          from: 'partial',
          to: 'full'
        }
      },
      ipAddress: '192.168.1.110',
      userAgent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7)'
    },
    {
      id: 'audit_006',
      timestamp: '2025-10-14T09:10:55Z',
      actionType: 'permission_set_cloned',
      actionLabel: 'Permission Set Cloned',
      user: {
        id: 'user_002',
        name: 'John Doe',
        email: 'john.doe@company.com'
      },
      target: {
        type: 'permission_set',
        id: 'ps_007',
        name: 'Sales Team Standard (Copy)'
      },
      changes: {
        clonedFrom: 'Sales Team Standard'
      },
      ipAddress: '192.168.1.105',
      userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64)'
    },
    {
      id: 'audit_007',
      timestamp: '2025-10-13T15:35:42Z',
      actionType: 'field_permissions_changed',
      actionLabel: 'Field Permissions Changed',
      user: {
        id: 'user_001',
        name: 'Jane Smith',
        email: 'jane.smith@company.com'
      },
      target: {
        type: 'field',
        id: 'field_002',
        name: 'Tax ID',
        objectName: 'Account'
      },
      changes: {
        permissions: {
          canRead: {
            from: ['admin', 'finance'],
            to: ['admin', 'finance', 'sales_manager']
          }
        }
      },
      ipAddress: '192.168.1.100',
      userAgent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7)'
    },
    {
      id: 'audit_008',
      timestamp: '2025-10-13T11:28:19Z',
      actionType: 'permission_set_deleted',
      actionLabel: 'Permission Set Deleted',
      user: {
        id: 'user_001',
        name: 'Jane Smith',
        email: 'jane.smith@company.com'
      },
      target: {
        type: 'permission_set',
        id: 'ps_005',
        name: 'Data Migration (Deprecated)'
      },
      changes: null,
      ipAddress: '192.168.1.100',
      userAgent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7)'
    },
    {
      id: 'audit_009',
      timestamp: '2025-10-12T13:42:07Z',
      actionType: 'user_removed_from_permission_set',
      actionLabel: 'User Removed from Permission Set',
      user: {
        id: 'user_002',
        name: 'John Doe',
        email: 'john.doe@company.com'
      },
      target: {
        type: 'permission_set',
        id: 'ps_003',
        name: 'Finance Read-Only'
      },
      changes: {
        assignedUsers: {
          removed: ['Bob Johnson']
        }
      },
      ipAddress: '192.168.1.105',
      userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64)'
    },
    {
      id: 'audit_010',
      timestamp: '2025-10-12T10:15:33Z',
      actionType: 'field_created',
      actionLabel: 'Field Created',
      user: {
        id: 'user_001',
        name: 'Jane Smith',
        email: 'jane.smith@company.com'
      },
      target: {
        type: 'field',
        id: 'field_013',
        name: 'Customer Segment',
        objectName: 'Account'
      },
      changes: {
        fieldType: 'Picklist',
        classification: 'internal'
      },
      ipAddress: '192.168.1.100',
      userAgent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7)'
    }
  ];

  // Apply client-side filtering for mock data
  let filtered = [...mockAuditEntries];

  if (filters.actionType) {
    filtered = filtered.filter(entry => entry.actionType === filters.actionType);
  }

  if (filters.userId) {
    filtered = filtered.filter(entry => entry.user.id === filters.userId);
  }

  return {
    data: filtered,
    pagination: {
      page: filters.page || 1,
      pageSize: filters.pageSize || 20,
      totalItems: filtered.length,
      totalPages: Math.ceil(filtered.length / (filters.pageSize || 20)),
      hasNext: false,
      hasPrev: false
    },
    _usingMockData: true
  };
};

/**
 * Fetch audit log entry details
 */
export const fetchAuditEntryDetails = async (auditId: string) => {
  console.log('🔍 Fetching audit entry details:', auditId);

  try {
    const response = await fetch(`${API_BASE_URL}/admin/audit/${auditId}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (response.ok) {
      console.log('✅ Audit entry details loaded from backend');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, using mock audit entry details');
  }

  // Mock detailed audit entry
  await new Promise(resolve => setTimeout(resolve, 100));

  // For now, return the same data (we'll enhance this in Batch 8B)
  const allEntries = await fetchAuditLog();
  const entry = allEntries.data.find((e: any) => e.id === auditId);

  if (!entry) {
    throw new Error('Audit entry not found');
  }

  return {
    data: entry,
    _usingMockData: true
  };
};
